import { Event } from "@workspace/api-client-react";

// Group key for artist nesting: the performer name when known. When performer is
// null (stale events that pre-date the performer-lifting refresh), fall back to
// extracting the artist from the label, and only then to the whole label.
export function artistKey(e: Event): string {
  const p = e.performer?.trim();
  if (p) return p;
  return extractArtistFromLabel(e.label, e.venue) ?? e.label;
}

// Matches a date-like suffix such as "July 4", "JUN 12", "December", or
// "Jul 4th" — a month name (full or abbreviated) optionally followed by a day
// number. Used to recognise the "<artist> <venue> - <date>" label shape.
const DATE_SUFFIX_RE =
  /^(jan(uary)?|feb(ruary)?|mar(ch)?|apr(il)?|may|jun(e)?|jul(y)?|aug(ust)?|sep(t(ember)?)?|oct(ober)?|nov(ember)?|dec(ember)?)\.?(\s+\d{1,2}(st|nd|rd|th)?)?$/i;

function isDateLike(s: string): boolean {
  return DATE_SUFFIX_RE.test(s.trim());
}

// Belt-and-suspenders client fallback for events whose performer is still null.
// Labels look like "HARRY STYLES WEMBLEY - JUN 12"; the artist is everything
// before the " - " minus the trailing venue token. We strip that token when
// either the event's venue is known AND the last word before the dash is part
// of that venue, OR the suffix after the dash is a date (e.g. "July 4") — which
// signals the "<artist> <venue> - <date>" shape even when no venue is attached
// yet. Otherwise extraction is ambiguous and we bail (returning null) so the
// caller falls back to the full label rather than mis-grouping.
export function extractArtistFromLabel(
  label: string,
  venue?: string | null,
): string | null {
  const dashIdx = label.indexOf(" - ");
  if (dashIdx === -1) return null;
  const beforeDash = label.slice(0, dashIdx).trim();
  const afterDash = label.slice(dashIdx + 3).trim();
  const words = beforeDash.split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;
  const lastWord = words[words.length - 1].toLowerCase();
  const v = venue?.trim();
  const venueConfirms = v
    ? new Set(v.toLowerCase().split(/\s+/).filter(Boolean)).has(lastWord)
    : false;
  if (!venueConfirms && !isDateLike(afterDash)) return null;
  const artist = words.slice(0, -1).join(" ").trim();
  return artist || null;
}

// Group events by artist, preserving the incoming order both of groups (by
// first appearance) and of events within each group. Keys are compared
// case-insensitively so a performer backfilled as "HARRY STYLES" lands in the
// same bucket as an extracted "Harry Styles"; the first-seen casing is used for
// the displayed header.
export function groupByArtist(events: Event[]): { artist: string; events: Event[] }[] {
  const map = new Map<string, { artist: string; events: Event[] }>();
  for (const e of events) {
    const display = artistKey(e);
    const key = display.toLowerCase();
    const bucket = map.get(key);
    if (bucket) bucket.events.push(e);
    else map.set(key, { artist: display, events: [e] });
  }
  return [...map.values()];
}
